import { useEffect } from 'react';
import { Box } from '@mui/material';
import { useHistory, useLocation, matchPath } from 'react-router-dom';

import {
    getDestinationUrl,
    FUNNEL_WORKFLOW_DESTINATIONS,
    type FunnelWorkflowDestination,
} from '../../utils/destinationMapping';

import { LayoutProvider } from './LayoutContext';
import Sidebar from './Sidebar';
import MobileSidebar from './Sidebar/Mobile';
import Content from './Content';
import RightSidebar from './RightSidebar';

import useThemeBreakpoint from 'modules/common/useThemeBreakpoint';
import usePrefetchZDUserData from 'services/help-center/prefetch/usePrefetchZDUserData';
import useAuthSelectors from 'modules/Auth/AuthProvider/useAuthSelector';
import PendingPaymentModal from 'modules/common/components/Payment/PendingPaymentModal';
import UpdatePaymentModal from 'modules/common/components/Payment/UpdatePaymentModal';
import ReferralPrompt from 'modules/Referrals/ReferralPrompt';
import { useMember } from 'modules/common/queries/member';
import OnboardingSetupCSAT from 'modules/common/components/CSatSurvey/OnboardingSetupCSAT';
import useOAuth from 'services/oauth/queries/useOAuth';
import { lastActiveRoute } from 'modules/Auth/AuthProvider/utils';
import { useAuthContext } from 'modules/Auth/AuthProvider';
import { fetchAuthenticationData } from 'modules/Auth/AuthProvider/useAuthHandlers';
import { MemberStatus } from 'modules/HubV2/common/interfaces/member';

export default function Layout({ children }: { children: React.ReactNode }) {
    const breakpoint = useThemeBreakpoint();
    const history = useHistory();
    const { isImpersonating, user } = useAuthSelectors('isImpersonating', 'user', 'token');
    const location = useLocation();
    const { dispatch, actions } = useAuthContext();

    // Prefetch member data for validations
    const { data: member, isPending: isMemberPending, failureCount: memberFailureCount } = useMember();
    useOAuth();
    usePrefetchZDUserData();

    useEffect(() => {
        const employeeRoutes = ['/hub-v2', '/hub', '/client-info', '/sales-portal'];

        if (!user) {
            lastActiveRoute.set();
            history.push('/login');
            return;
        }

        if (user?.is_employee) {
            if (!isImpersonating && employeeRoutes.findIndex((route) => location.pathname.startsWith(route)) === -1) {
                const redirectPath =
                    user.groups?.includes('SalesAdmin') && !user.groups?.includes('HubAdmin')
                        ? '/sales-portal'
                        : '/hub-v2';
                history.replace(redirectPath);
            }
            return;
        }

        // Force refresh user data if destination seems stale or missing
        if (user?.email && !user?.destination) {
            fetchAuthenticationData(actions, dispatch);
            return;
        }

        // Member mainstep redirect
        if (
            member?.status &&
            member?.status !== MemberStatus.PENDING &&
            member?.mainstep &&
            member?.mainstep !== 'dashboard' &&
            matchPath(location.pathname, `/${member.mainstep}`) === null
        ) {
            history.push(`/${member.mainstep}`);
            return;
        }

        // Destination-based redirects for funnel workflow users
        if (user?.destination && FUNNEL_WORKFLOW_DESTINATIONS.includes(user.destination as FunnelWorkflowDestination)) {
            const destinationUrl = getDestinationUrl(user.destination);

            const isOnDestinationPath = matchPath(location.pathname, { path: destinationUrl, exact: false });

            if (!isOnDestinationPath) {
                history.replace(destinationUrl);
            }
        }

        // Personal info destination redirect (user but no active funnel workflow)
        if (user?.destination === 'personal_info') {
            const isOnPersonalInfoPath = matchPath(location.pathname, {
                path: '/sign-up/personal-information',
                exact: false,
            });

            if (!isOnPersonalInfoPath) {
                // Check if we should request user's name (first_name may be set via Google signup)
                const shouldRequestName = !user?.first_name;

                history.replace('/sign-up/personal-information', {
                    shouldRequestName,
                });
            }
        }
    }, [user, member, history, location.pathname, isImpersonating, dispatch, actions]);

    if (!user) {
        return null;
    }

    // Synchronous redirect logic to prevent flash
    const employeeRoutes = ['/hub-v2', '/hub', '/client-info', '/sales-portal'];

    // Check if user is an employee and should be redirected
    if (
        user?.is_employee &&
        !isImpersonating &&
        employeeRoutes.findIndex((route) => location.pathname.startsWith(route)) === -1
    ) {
        const redirectPath =
            user.groups?.includes('SalesAdmin') && !user.groups?.includes('HubAdmin') ? '/sales-portal' : '/hub-v2';
        history.replace(redirectPath);
        return null;
    }

    // `members/` has to have resolved before the shell renders, because the redirect
    // effect above decides on `member.mainstep`.
    //
    // Wait for the first attempt only. The query retries with backoff and stays
    // `pending` throughout, so blocking on `isPending` alone would hold a blank page for
    // seconds. `failureCount` rises to 1 while the status is still `pending`, which is
    // what makes a single-attempt wait expressible. Everything below tolerates a missing
    // member, so a degraded shell beats a blank screen.
    if (isMemberPending && memberFailureCount === 0) {
        return null;
    }

    return (
        <LayoutProvider>
            <Box aria-label="Main Page" sx={{ backgroundColor: 'cement.50', display: 'flex', height: '100dvh' }}>
                {breakpoint !== 'small' && <Sidebar />}
                {breakpoint === 'small' && <MobileSidebar />}
                <Content>{children}</Content>

                <UpdatePaymentModal />
                <PendingPaymentModal />
                <ReferralPrompt />

                {/* Unified onboarding CSAT - triggers after all setups complete */}
                <OnboardingSetupCSAT />

                <RightSidebar variant={member?.hasPaymentIssue ? 'temporary' : 'persistent'} />
            </Box>
        </LayoutProvider>
    );
}
